import { ref, uploadBytes } from 'firebase/storage';
import { storage } from '../firebase/config';

const FAILED_RETRY_MS = 6 * 60 * 60 * 1000;
const inFlightInitKeys = new Set();

const DEFAULT_SECTIONS = [
  'aportes',
  'recursos',
  'partituras',
  'musicxml',
  'audio',
  'video',
  'imagenes',
  'otros'
];

const getMonthKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return { year, month, key: `${year}-${month}` };
};

/** Apps que no usan el workspace de archivos de Aula (aportes/partituras/…). */
const APPS_WITHOUT_AULA_WORKSPACE = new Set([
  'bordona',
  'play',
  'junior',
  'landing',
  'melody',
  'atlas',
  'decimas',
  'modo',
]);

const shouldInitWorkspace = () => {
  const forceInit = import.meta.env.VITE_STORAGE_INIT_FORCE === '1';
  if (forceInit) return true;

  // En desarrollo (especialmente Codespaces/GitHub Dev), evitar preflights CORS ruidosos.
  if (import.meta.env.DEV) return false;

  const appTarget = String(import.meta.env.VITE_APP_TARGET || '').toLowerCase();
  if (APPS_WITHOUT_AULA_WORKSPACE.has(appTarget)) return false;

  return true;
};

const now = () => Date.now();

const createPlaceholder = async (path) => {
  const blob = new Blob([''], { type: 'text/plain' });
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, blob, {
    contentType: 'text/plain',
    cacheControl: 'public,max-age=3600'
  });
};

export const ensureUserStorageWorkspace = async (uid, sections = DEFAULT_SECTIONS) => {
  if (!uid) return;
  if (!shouldInitWorkspace()) return;

  const { year, month, key } = getMonthKey();
  const localKey = `storageWorkspaceInit:${uid}:${key}`;
  const failKey = `storageWorkspaceInitFail:${uid}:${key}`;

  if (inFlightInitKeys.has(localKey)) return;

  try {
    if (localStorage.getItem(localKey)) return;

    const failAtRaw = localStorage.getItem(failKey);
    const failAt = failAtRaw ? Number(failAtRaw) : 0;
    if (Number.isFinite(failAt) && failAt > 0 && now() - failAt < FAILED_RETRY_MS) {
      return;
    }
  } catch {
    // ignore
  }

  inFlightInitKeys.add(localKey);

  try {
    const tasks = sections.map((section) => {
      const safeSection = String(section || '').trim();
      if (!safeSection) return Promise.resolve();
      const path = `users/${uid}/${safeSection}/${year}/${month}/.keep`;
      return createPlaceholder(path);
    });
    await Promise.all(tasks);
    try {
      localStorage.setItem(localKey, '1');
      localStorage.removeItem(failKey);
    } catch {
      // ignore
    }
  } catch (error) {
    try {
      localStorage.setItem(failKey, String(now()));
    } catch {
      // ignore
    }

    const message = String(error?.message || error || '').toLowerCase();
    const isCorsLike =
      message.includes('cors') ||
      message.includes('preflight') ||
      message.includes('xmlhttprequest') ||
      message.includes('network request failed');

    if (isCorsLike) {
      console.warn('⚠️ Storage workspace init omitido por CORS en este entorno');
    } else {
      console.warn('⚠️ No se pudo inicializar el workspace de Storage:', error);
    }
  } finally {
    inFlightInitKeys.delete(localKey);
  }
};

export const buildUserStoragePath = ({ uid, section, itemId, date = new Date() }) => {
  const { year, month } = getMonthKey(date);
  const safeSection = String(section || 'otros').trim() || 'otros';
  const safeItem = String(itemId || '').trim();
  if (!uid) return '';
  if (!safeItem) return `users/${uid}/${safeSection}/${year}/${month}`;
  return `users/${uid}/${safeSection}/${year}/${month}/${safeItem}`;
};

export const STORAGE_SECTIONS = DEFAULT_SECTIONS;

export default {
  ensureUserStorageWorkspace,
  buildUserStoragePath,
  STORAGE_SECTIONS
};
